/**
 * Alert Recipients — who receives the emails no person triggered.
 *
 * Low-stock warnings, licence-expiry copies, vendor tax-exemption warnings,
 * bounce alerts and break-glass security alerts had their recipients baked
 * into code. Each one can now be redirected, extended or switched off here.
 * "Currently goes to" is resolved live by the backend, so what you see is
 * exactly who would be emailed if the alert fired this second.
 */
import React, { useEffect, useState } from 'react';
import { alertRecipientsAPI } from '../lib/api';
import { useCap } from '../components/auth/RequireCap';
import AccessState from '../components/auth/AccessState';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { BellRing, Users } from 'lucide-react';

const MODE_LABEL = {
  default: 'Built-in default',
  custom: 'Only the people I list',
  both: 'Default plus the people I list',
};

function AlertRow({ alert, canEdit, onSaved }) {
  const [mode, setMode] = useState(alert.mode);
  const [text, setText] = useState((alert.recipients || []).join(', '));
  const [enabled, setEnabled] = useState(alert.enabled);
  const [saving, setSaving] = useState(false);

  const dirty = mode !== alert.mode || enabled !== alert.enabled
    || text !== (alert.recipients || []).join(', ');

  const save = async () => {
    setSaving(true);
    try {
      const recipients = text.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
      const { data } = await alertRecipientsAPI.update(alert.key, { mode, recipients, enabled });
      toast.success(data.resolved.length
        ? `Saved — now goes to ${data.resolved.join(', ')}`
        : 'Saved — this alert will not be emailed to anyone');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const showList = mode === 'custom' || mode === 'both';

  return (
    <Card data-testid={`alert-row-${alert.key}`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-medium">{alert.label}</h3>
              <Badge variant="outline" className="text-xs">{alert.group}</Badge>
            </div>
            <p className="text-sm text-fg-muted mt-1 max-w-2xl">{alert.description}</p>
          </div>
          {!alert.cannot_disable && (
            <div className="flex items-center gap-2 shrink-0">
              <Label className="text-xs text-fg-muted">{enabled ? 'On' : 'Off'}</Label>
              <Switch checked={enabled} onCheckedChange={setEnabled} disabled={!canEdit}
                data-testid={`alert-enabled-${alert.key}`} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Send to</Label>
            <Select value={mode} onValueChange={setMode} disabled={!canEdit || !enabled}>
              <SelectTrigger data-testid={`alert-mode-${alert.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MODE_LABEL).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-fg-muted mt-1">Default: {alert.default_note}</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">
              {showList ? 'Addresses (comma separated)' : 'Addresses (switch mode to add)'}
            </Label>
            <Input value={text} onChange={(e) => setText(e.target.value)}
              disabled={!canEdit || !showList || !enabled}
              placeholder="name@tricorps.com, someone.else@tricorps.com"
              data-testid={`alert-recipients-${alert.key}`} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 pt-1 border-t border-line-subtle">
          <p className="text-xs text-fg-muted flex items-center gap-1.5 min-w-0 pt-3"
            data-testid={`alert-resolved-${alert.key}`}>
            <Users className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              {enabled && alert.resolved?.length
                ? <>Currently goes to <b>{alert.resolved.join(', ')}</b></>
                : 'Nobody is emailed for this alert'}
            </span>
          </p>
          {canEdit && (
            <Button size="sm" onClick={save} disabled={!dirty || saving} className="mt-3 shrink-0"
              data-testid={`alert-save-${alert.key}`}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AlertRecipients() {
  const canView = useCap('admin', 'view');
  const canEdit = useCap('admin', 'edit');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await alertRecipientsAPI.list();
      setAlerts(data.alerts);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load alert recipients');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); }, [canView]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="space-y-6" data-testid="alert-recipients-page">
        <AccessState module="admin" action="view" items={[]} denied
          noAccessTitle="No access to Alert Recipients"
          noAccessDescription="Who receives system alerts is an administration setting. Ask an administrator to add you to an alert." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alert-recipients-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <BellRing className="w-7 h-7 text-slate-400" />Alert Recipients
        </h1>
        <p className="text-base text-fg-muted mt-1 max-w-3xl">
          These are the emails nobody triggers by hand — they fire on a schedule or off a
          stock level. Redirect any of them without a code change. Emails that answer
          something a person did (work-order approvals, evaluation requests) always go to
          the people involved and are not listed here.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <div className="space-y-4">
          {alerts.map(a => (
            <AlertRow key={a.key} alert={a} canEdit={canEdit} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}
